import { CheckCircle2, Clock4, AlertCircle } from 'lucide-react'
import { cn, formatCurrency, formatHours } from '@/lib/utils'
import { useGetProjectDetailsQuery } from '@/redux/service/project'
import {
  useGetVendorPaymentsQuery,
  type VendorPayment,
  type VendorPaymentStatus,
} from '@/redux/service/vendor'
import { findFullPaymentRecord } from './vendorPaymentShared'
import { Skeleton } from './ui/skeleton'
import { useCheckPermission } from '@/hooks/useCheckPermission'

interface VendorMilestonesProps {
  projectId: number
  vendorName: string
  paymentType: 'full' | 'milestone'
}

const fmtDate = (dateString: string | null | undefined) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const STATUS_STYLES: Record<
  VendorPaymentStatus,
  { label: string; className: string; icon: typeof Clock4 }
> = {
  paid: {
    label: 'Paid',
    className:
      'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    icon: CheckCircle2,
  },
  partial: {
    label: 'Partial',
    className:
      'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    icon: Clock4,
  },
  pending: {
    label: 'Pending',
    className:
      'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    icon: Clock4,
  },
  unset: {
    label: 'Amount not set',
    className: 'bg-muted text-muted-foreground border-border',
    icon: AlertCircle,
  },
}

const StatusBadge = ({ status }: { status: VendorPaymentStatus }) => {
  const style = STATUS_STYLES[status]
  const StatusIcon = style.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border',
        style.className
      )}
    >
      <StatusIcon className='w-2.5 h-2.5' />
      {style.label}
    </span>
  )
}

interface ClientBudgetItem {
  key: string
  name: string
  budget: number | string | undefined
  hours: number | undefined
  rate: number | undefined
  milestoneNames: string[]
}

const ColumnHeader = ({ dot, label }: { dot: string; label: string }) => (
  <div className='flex items-center gap-2 text-xs font-semibold text-muted-foreground'>
    <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', dot)} />
    {label}
  </div>
)

const EmptyBox = ({ message }: { message: string }) => (
  <div className='flex flex-col items-center justify-center gap-1.5 py-10 border border-dashed border-border rounded-2xl'>
    <p className='text-sm font-medium text-muted-foreground'>{message}</p>
  </div>
)

const VendorMilestones = ({
  projectId,
  vendorName,
  paymentType,
}: VendorMilestonesProps) => {
  const { hasPageAccess } = useCheckPermission()
  const canViewClientFinance =
    hasPageAccess('finance') || hasPageAccess('mini_finance')
  const canViewVendorFinance = hasPageAccess('finance')

  const { data: detailsData, isLoading: detailsLoading } =
    useGetProjectDetailsQuery({ projectId, limit: 100 }, { skip: !projectId })
  const { data: paymentsData, isLoading: paymentsLoading } =
    useGetVendorPaymentsQuery({ projectId }, { skip: !projectId })

  const isLoading = detailsLoading || paymentsLoading
  const details = detailsData?.data ?? []
  const payments = paymentsData?.data ?? []

  // These two sides are unrelated data sets - client budget entries (this
  // system's own budget/milestone data) and vendor payment milestones
  // (ClickUp-synced, tracked separately on the vendor side) - there's no
  // reliable link between them, so they're just listed independently, not
  // paired up.
  //
  // The budget itself lives on the Project Details row, not per milestone -
  // one row can list several milestone names in its `milestones` array, all
  // covered by that single budget/hours/rate. So it's one box per budget
  // entry, with the milestone names shown as tags inside it, rather than a
  // separate box per milestone name.
  const clientBudgetEntries: ClientBudgetItem[] = details.map((d, idx) => ({
    key: String(d.id ?? idx),
    name: d.description ?? `Budget Entry ${idx + 1}`,
    budget: d.total_budget,
    hours: d.total_hours,
    rate: d.hourly_cost,
    milestoneNames: (d.milestones ?? []).map(m => m.name),
  }))

  const vendorItems: VendorPayment[] =
    paymentType === 'full'
      ? (() => {
          const fullPayment = findFullPaymentRecord(payments)
          return fullPayment ? [fullPayment] : []
        })()
      : payments

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* ── Client column ── */}
        <div className='space-y-3'>
          <ColumnHeader dot='bg-emerald-500' label='Client Budget' />
          {isLoading ? (
            <div className='space-y-3'>
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className='h-24 w-full rounded-2xl' />
              ))}
            </div>
          ) : clientBudgetEntries.length === 0 ? (
            <EmptyBox message='No budget entries yet' />
          ) : (
            <div className='space-y-3'>
              {clientBudgetEntries.map(entry => (
                <div
                  key={entry.key}
                  className='rounded-2xl border border-border/60 shadow-sm bg-card p-5'
                >
                  <div className='flex items-center gap-1.5 mb-2'>
                    <span className='w-2 h-2 rounded-full bg-emerald-500 shrink-0' />
                    <span className='text-[10px] font-bold uppercase tracking-wide text-muted-foreground'>
                      Client
                    </span>
                  </div>
                  <h4
                    className='font-bold text-base text-foreground mb-3 truncate'
                    title={entry.name}
                  >
                    {entry.name}
                  </h4>
                  <div className='flex flex-wrap gap-6 mb-3'>
                    <div>
                      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1'>
                        Budget
                      </p>
                      <p className='text-sm font-bold text-emerald-600'>
                        {canViewClientFinance && entry.budget != null
                          ? formatCurrency(entry.budget)
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1'>
                        Hours
                      </p>
                      <p className='text-sm font-bold text-foreground'>
                        {entry.hours != null
                          ? `${formatHours(entry.hours)} hr`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1'>
                        Rate
                      </p>
                      <p className='text-sm font-bold text-foreground'>
                        {canViewClientFinance && entry.rate != null
                          ? `${formatCurrency(entry.rate)}/hr`
                          : '—'}
                      </p>
                    </div>
                  </div>
                  {entry.milestoneNames.length > 0 && (
                    <div className='flex flex-wrap gap-1.5 pt-3 border-t border-border/50'>
                      {entry.milestoneNames.map(name => (
                        <span
                          key={name}
                          className='inline-flex items-center px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20 text-xs font-medium'
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Vendor column ── */}
        <div className='space-y-3'>
          <ColumnHeader dot='bg-red-500' label='Vendor Milestones' />
          {isLoading ? (
            <div className='space-y-3'>
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className='h-24 w-full rounded-2xl' />
              ))}
            </div>
          ) : vendorItems.length === 0 ? (
            <EmptyBox message='No vendor milestones yet' />
          ) : (
            <div className='space-y-3'>
              {vendorItems.map(p => (
                <div
                  key={p.id}
                  className='rounded-2xl border border-border/60 shadow-sm bg-card p-5'
                >
                  <div className='flex items-center gap-1.5 mb-2'>
                    <span className='w-2 h-2 rounded-full bg-red-500 shrink-0' />
                    <span className='text-[10px] font-bold uppercase tracking-wide text-muted-foreground'>
                      Vendor · {vendorName}
                    </span>
                  </div>
                  <h4
                    className='font-bold text-base text-foreground mb-3 truncate'
                    title={p.milestone_name}
                  >
                    {p.milestone_name}
                  </h4>
                  <div className='flex flex-wrap gap-6 items-start'>
                    <div>
                      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1'>
                        Amount
                      </p>
                      <p className='text-sm font-bold text-red-600'>
                        {canViewVendorFinance && p.amount != null
                          ? formatCurrency(p.amount)
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1'>
                        Released
                      </p>
                      <p className='text-sm font-bold text-foreground'>
                        {canViewVendorFinance
                          ? formatCurrency(p.released ?? 0)
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1'>
                        Dates
                      </p>
                      <p className='text-sm font-medium text-foreground whitespace-nowrap'>
                        {p.start_date || p.end_date
                          ? `${fmtDate(p.start_date)} – ${fmtDate(p.end_date)}`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1'>
                        Status
                      </p>
                      <StatusBadge status={p.status ?? 'unset'} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VendorMilestones
